'use client'

import { chatPrefillMark } from '@/app/lib/guideChat/prefill'
import type { CollectedFieldName } from '@/app/lib/guideChat/types'

import { useForm, FormProvider, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@/app/lib/zodResolver'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import { isExpatriateResidence } from '@/app/utils/loan/residence-address'
import React, { useEffect, useLayoutEffect, forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import {
    borrowerFormSchema,
    type BorrowerFormData,
    createBorrowerFormDefaults,
} from '@/app/schemas/loan-comparator/borrower.schema'
import {
    LIBERAL_JOB_ID,
    MEDICAL_JOB_ID,
    RETIRED_JOB_ID,
    UNEMPLOYED_JOB_ID,
    EMPLOYEE_JOB_ID,
    HANDWORK_JOBS,
    IN_HEIGHT_JOBS,
    RETIRED_EXCLUDED_JOBS,
    SPORT_NONE_VALUE,
} from '@/app/constants/loanTypes'
import YesNoSwitch from '@/app/components/atomic/molecules/YesNoSwitch'
import DateField from '@/app/components/healthComparator/steps/DateField'
import AppSelect from '@/app/components/atomic/AppSelect'
import LoanFormField from '@/app/components/loanComparator/fields/LoanFormField'
import LoanFieldFamily, { type LoanFamilyQuestion } from '@/app/components/loanComparator/fields/LoanFieldFamily'
import type { IBorrower } from '@/app/models/loanQuote'
import { formatDateToDDMMYYYY } from '@/app/utils/helpers'

type RiskySportOption = { label: string; value: number }

const readableSportId = (sport: unknown, options: RiskySportOption[]): string | undefined => {
    const first = Array.isArray(sport) ? sport[0] : undefined
    return options.some((option) => option.value.toString() === String(first)) ? String(first) : undefined
}

// A sport stored before the current format cannot be read back, and is worth more than a cleared answer
const isUnreadableStoredSport = (sport: unknown, options: RiskySportOption[]): boolean => {
    if (sport == null || (Array.isArray(sport) && sport.length === 0)) return false
    return readableSportId(sport, options) === undefined
}

// Exposed methods for parent component
export interface BorrowerFormHandle {
    validate: () => Promise<boolean>
    getData: () => BorrowerFormData
    reset: () => void
    getErrorMessages: () => string[]
}

interface Props {
    isMainBorrower?: boolean
    className?: string
}

const LoanComparatorQuotationBorrowerForm = forwardRef<BorrowerFormHandle, Props>(
    ({ isMainBorrower = true, className = '' }, ref) => {
        const quote = useLoanComparatorStore((state) => state.quote)
        const updateMain = useLoanComparatorStore((state) => state.borrower.updateMain)
        const updateCo = useLoanComparatorStore((state) => state.borrower.updateCo)
        const formOptions = useLoanComparatorStore((state) => state.formOptions)
        const globalOptions = useLoanComparatorStore((state) => state.globalOptions)

        // Transform lists from store to format expected by select
        const jobOptions = useMemo(() => {
            if (!formOptions?.jobList) return []
            return formOptions.jobList.map((job) => ({
                label: job.name,
                value: job.id,
            }))
        }, [formOptions])
        const liberalJobOptions = useMemo(() => {
            if (!formOptions?.liberalJobList) return []
            return formOptions.liberalJobList.map((job) => ({
                label: job.name,
                value: job.id,
            }))
        }, [formOptions])
        const medicalJobOptions = useMemo(() => {
            if (!formOptions?.medicalJobList) return []
            return formOptions.medicalJobList.map((job) => ({
                label: job.label,
                value: job.value,
            }))
        }, [formOptions])
        const riskFieldOptions = useMemo(() => {
            if (!formOptions?.jobRiskFieldsList) return []
            return formOptions.jobRiskFieldsList.map((field) => ({
                label: field.name,
                value: field.id,
            }))
        }, [formOptions])
        // « Je ne pratique aucun des sports de cette liste » duplicates the No answer of the switch
        const riskySportOptions = useMemo(() => {
            if (!globalOptions?.riskySportsList) return []
            return globalOptions.riskySportsList
                .filter((sport) => sport.value !== SPORT_NONE_VALUE)
                .map((sport) => ({ label: sport.label, value: sport.value }))
        }, [globalOptions])

        // Filter job list for retired (exclude retired and unemployed)
        const retiredJobOptions = useMemo(() => {
            return jobOptions.filter((job) => !RETIRED_EXCLUDED_JOBS.includes(job.value))
        }, [jobOptions])

        // Transform country list from globalOptions. SUBAREA group = territoires français
        const subareaCountries = useMemo(() => {
            if (!globalOptions?.countryList || !globalOptions?.livingAreaTypes) return []
            return globalOptions.countryList[globalOptions.livingAreaTypes.SUBAREA] || []
        }, [globalOptions])

        const expatCountries = useMemo(() => {
            if (!globalOptions?.countryList || !globalOptions?.livingAreaTypes) return []
            return globalOptions.countryList[globalOptions.livingAreaTypes.EXPATRIATE] || []
        }, [globalOptions])

        const countryOptions = useMemo(() => {
            // Corse + DROM-COM puis Monaco en tête de liste, le reste en alphabétique
            const priorityRank = (option: { frenchTerritory: boolean; code: string }) => {
                if (option.frenchTerritory) return 0
                if (option.code === 'MC') return 1
                return 2
            }
            const sorted = [
                ...subareaCountries.map((country) => ({
                    label: country.FR,
                    value: country.FR,
                    code: country.A2,
                    frenchTerritory: true,
                })),
                ...expatCountries.map((country) => ({
                    label: country.FR,
                    value: country.FR,
                    code: country.A2,
                    frenchTerritory: false,
                })),
            ].sort((a, b) => priorityRank(a) - priorityRank(b) || a.label.localeCompare(b.label, 'fr'))
            // 'FRANCE' est la chaîne magique historique (parité Vue) : payload address.country='FRANCE', sub_area=null
            return [
                {
                    label: 'France continentale',
                    value: 'FRANCE',
                    code: 'FR',
                    frenchTerritory: true,
                },
                ...sorted,
            ]
        }, [subareaCountries, expatCountries])

        // Transform workingHeightRangesList from globalOptions
        const workingHeightOptions = useMemo(() => {
            if (!globalOptions?.workingHeightRangesList) return []

            return Object.entries(globalOptions.workingHeightRangesList).map(([key, label]) => ({
                label: label as string,
                value: parseInt(key, 10),
            }))
        }, [globalOptions])

        // Transform annualMileageRangesList from globalOptions
        const annualMileageOptions = useMemo(() => {
            if (!globalOptions?.annualMileageRangesList) return []

            return Object.entries(globalOptions.annualMileageRangesList).map(([key, label]) => ({
                label: label as string,
                value: parseInt(key, 10),
            }))
        }, [globalOptions])

        // Get the appropriate borrower data
        const borrowerData = isMainBorrower ? quote?.main_borrower : quote?.co_borrower

        const prefilledByChat = useLoanComparatorStore((state) => state.prefilledByChat)
        const CO_FIELDS: Partial<Record<CollectedFieldName, CollectedFieldName>> = { birthDate: 'coBirthDate', smoker: 'coSmoker', profession: 'coProfession' }
        const chatMark = (name: CollectedFieldName) => {
            if (isMainBorrower) return chatPrefillMark(prefilledByChat, name)
            const coName = CO_FIELDS[name]
            return coName ? chatPrefillMark(prefilledByChat, coName) : null
        }

        // Compute initial values for form
        const initialValues = useMemo((): Partial<BorrowerFormData> => {
            if (borrowerData) {
                let jobId: string | number = ''
                if (borrowerData.job?.value) {
                    jobId = borrowerData.job.value
                } else if (borrowerData.type_work?.id) {
                    jobId = borrowerData.type_work.id
                }

                let liberalJobId: string | number = ''
                if (borrowerData.liberal_job?.value) {
                    liberalJobId = borrowerData.liberal_job.value
                } else if (borrowerData.lib_type_work?.id) {
                    liberalJobId = borrowerData.lib_type_work.id
                }

                const rawBirthdate = borrowerData.birthdate || borrowerData.prospect_info?.birthday_date || ''
                const birthdate = formatDateToDDMMYYYY(rawBirthdate)

                const storedRiskySport = readableSportId(borrowerData.sport, riskySportOptions)

                return {
                    birthdate,
                    job_id: jobId?.toString() || '',
                    smoker: borrowerData.smoker || false,
                    medical_risk: borrowerData.medical_risk || false,
                    job_risk: borrowerData.has_risky_job ?? borrowerData.job_risk ?? false,
                    risky_sport: storedRiskySport !== undefined,
                    risky_sport_name: storedRiskySport,
                    address_country:
                        borrowerData.prospect_info?.address_sub_area ||
                        borrowerData.prospect_info?.address_country ||
                        'FRANCE',
                    liberal_job: liberalJobId ? liberalJobId.toString() : undefined,
                    medical_job:
                        typeof borrowerData.medical_job === 'object'
                            ? borrowerData.medical_job?.value?.toString()
                            : borrowerData.medical_job?.toString() || undefined,
                    job_risk_field: borrowerData.risk_field?.id?.toString() || undefined,
                    job_clerk: borrowerData.clerk_job ?? undefined,
                    searching_job: borrowerData.looking_for_work ?? undefined,
                    job_handler: borrowerData.handwork ?? undefined,
                    job_trips: borrowerData.distance > 0,
                    working_height: borrowerData.working_height ?? undefined,
                    annual_mileage: borrowerData.annual_mileage ?? undefined,
                    rejected_by_insurance: borrowerData.rejected_by_insurance ?? undefined,
                }
            }
            return createBorrowerFormDefaults()
        }, [borrowerData, riskySportOptions])

        const methods = useForm<BorrowerFormData>({
            resolver: zodResolver(borrowerFormSchema),
            mode: 'onBlur',
            reValidateMode: 'onBlur',
            defaultValues: initialValues,
        })
        const {
            register,
            formState: { errors },
            setValue,
            watch,
            trigger,
            getValues,
            reset: resetForm,
        } = methods

        // Reset form when borrower data changes
        const prevDataRef = useRef<string | null>(null)

        useLayoutEffect(() => {
            const currentData = JSON.stringify({
                id: borrowerData?.id,
                birthdate: borrowerData?.birthdate || borrowerData?.prospect_info?.birthday_date,
                jobId: borrowerData?.type_work?.id,
                smoker: borrowerData?.smoker,
                medicalRisk: borrowerData?.medical_risk,
                jobRisk: borrowerData?.job_risk ?? borrowerData?.has_risky_job,
                handwork: borrowerData?.handwork,
            })

            const hasMeaningfulData =
                !!borrowerData?.id || borrowerData?.birthdate || borrowerData?.prospect_info?.birthday_date
            const dataChanged = currentData !== prevDataRef.current

            const formBirthdate = getValues('birthdate')
            const expectedBirthdate = initialValues.birthdate
            const birthdateMismatch = hasMeaningfulData && expectedBirthdate && formBirthdate !== expectedBirthdate

            const shouldReset = borrowerData && hasMeaningfulData && (dataChanged || birthdateMismatch)

            if (shouldReset) {
                resetForm(initialValues)
                prevDataRef.current = currentData

                if (initialValues.birthdate) {
                    const updateFn = isMainBorrower ? updateMain : updateCo
                    updateFn({
                        birthdate: initialValues.birthdate,
                        prospect_info: {
                            ...borrowerData?.prospect_info,
                            birthday_date: initialValues.birthdate,
                        },
                    } as Partial<IBorrower>)
                }
            }
        }, [borrowerData, initialValues, resetForm, isMainBorrower, updateMain, updateCo, getValues])

        const formatErrors = (err: FieldErrors): string[] => {
            return Object.entries(err).flatMap(([key, value]) => {
                if (!value) return []
                if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
                    return [`${key}: ${value.message}`]
                }
                if (typeof value === 'object') {
                    return formatErrors(value as FieldErrors).map((child) => `${key}.${child}`)
                }
                return []
            })
        }

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validate: async () => {
                return await trigger()
            },
            getData: () => {
                return getValues()
            },
            reset: () => {
                resetForm()
            },
            getErrorMessages: () => {
                return formatErrors(errors)
            },
        }))

        // Watch individual fields
        const birthdate = watch('birthdate')
        const job_id = watch('job_id')
        const smoker = watch('smoker')
        const medical_risk = watch('medical_risk')
        const job_risk = watch('job_risk')
        const liberal_job = watch('liberal_job')
        const medical_job = watch('medical_job')
        const address_country = watch('address_country')
        const job_handler = watch('job_handler')
        const job_risk_field = watch('job_risk_field')
        const risky_sport = watch('risky_sport')
        const risky_sport_name = watch('risky_sport_name')
        const job_clerk = watch('job_clerk')
        const searching_job = watch('searching_job')
        const working_height = watch('working_height')
        const annual_mileage = watch('annual_mileage')
        const rejected_by_insurance = watch('rejected_by_insurance')

        // Conditional visibility logic
        const currentJobId = job_id ? parseInt(job_id) : null
        const currentLiberalJobId = liberal_job ? parseInt(liberal_job) : null

        const hasJob = !!currentJobId
        const isLiberalJob = hasJob && currentJobId === LIBERAL_JOB_ID
        const isRetired = hasJob && currentJobId === RETIRED_JOB_ID
        const isUnemployed = !hasJob || currentJobId === UNEMPLOYED_JOB_ID
        const isNonExecutiveEmployee = hasJob && currentJobId === EMPLOYEE_JOB_ID
        const hasMedicalJob = isLiberalJob && currentLiberalJobId === MEDICAL_JOB_ID
        // Un retraité renseigne sa dernière profession dans le même champ `liberal_job` (même mapping
        // back), mais parmi les métiers, pas parmi les types de profession libérale
        const lastProfessionOptions = isRetired ? retiredJobOptions : liberalJobOptions
        const jobMayHaveHandwork = hasJob && HANDWORK_JOBS.includes(currentJobId)
        const jobMayHaveHeightRisk = hasJob && IN_HEIGHT_JOBS.includes(currentJobId)

        const showSearchingJob = hasJob && currentJobId === UNEMPLOYED_JOB_ID
        const showAnnualMileage = !isRetired && !isUnemployed
        const showJobRisk = !isRetired && !isUnemployed

        // Helper to normalize boolean values for the Oui/Non switch
        const normalizeBoolean = (value: any): boolean => {
            if (value === true || value === 'true') return true
            if (value === false || value === 'false') return false
            return false
        }

        // Sync birthdate to store once the date is complete (picker commits a full date)
        const syncBirthdateToStore = () => {
            const birthdateValue = getValues('birthdate')
            const updateFn = isMainBorrower ? updateMain : updateCo
            updateFn({
                birthdate: birthdateValue || null,
                prospect_info: {
                    ...borrowerData?.prospect_info,
                    birthday_date: birthdateValue || null,
                },
            } as Partial<IBorrower>)
        }

        useEffect(() => {
            if (birthdate && /^\d{2}\/\d{2}\/\d{4}$/.test(birthdate)) {
                syncBirthdateToStore()
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [birthdate])

        // Sync form changes to store (excluding birthdate)
        useEffect(() => {
            const selectedJob = job_id ? jobOptions.find((job) => job.value === parseInt(job_id)) : null
            const selectedLiberalJob = liberal_job
                ? lastProfessionOptions.find((job) => job.value === parseInt(liberal_job))
                : null
            const selectedMedicalJob = medical_job
                ? medicalJobOptions.find((job) => job.value === parseInt(medical_job))
                : null
            const selectedRiskField =
                job_risk_field && riskFieldOptions.length > 0
                    ? riskFieldOptions.find((field) => field.value.toString() === job_risk_field)
                    : null

            const isExpat = isExpatriateResidence(address_country, subareaCountries)

            const chosenSport = risky_sport && risky_sport_name ? risky_sport_name : null
            const keepsUnreadableSport =
                !chosenSport && isUnreadableStoredSport(borrowerData?.sport, riskySportOptions)

            const borrowerUpdate: Record<string, unknown> = {
                job: selectedJob || null,
                liberal_job: selectedLiberalJob || null,
                medical_job: selectedMedicalJob || null,
                smoker: smoker || false,
                medical_risk: medical_risk || false,
                job_risk: job_risk || false,
                has_risky_job: job_risk || false,
                is_expat: isExpat,
                handwork: job_handler || false,
                risk_field:
                    job_risk && selectedRiskField
                        ? { id: selectedRiskField.value, name: selectedRiskField.label }
                        : null,
                ...(keepsUnreadableSport ? {} : { sport: chosenSport ? [chosenSport] : [] }),
                clerk_job: job_clerk || false,
                looking_for_work: searching_job || false,
                working_height: working_height || 0,
                annual_mileage: annual_mileage || 0,
                rejected_by_insurance: (medical_risk === true && rejected_by_insurance) || false,
                prospect_info: {
                    ...borrowerData?.prospect_info,
                    address_country: address_country || 'FRANCE',
                    address_sub_area: address_country === 'FRANCE' ? null : address_country,
                },
            }

            if (isMainBorrower) {
                updateMain(borrowerUpdate as Partial<IBorrower>)
            } else {
                updateCo(borrowerUpdate as Partial<IBorrower>)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            job_id, liberal_job, medical_job, smoker, medical_risk, job_risk,
            address_country, subareaCountries, isMainBorrower, job_handler, job_risk_field,
            job_clerk, searching_job, working_height, annual_mileage, rejected_by_insurance,
            risky_sport, risky_sport_name, riskySportOptions,
        ])

        // Effect: Reset fields when job changes
        useEffect(() => {
            if (!currentJobId) return

            // Une valeur qui ne vient pas de la liste applicable (statut sans sous-profession, ou passage
            // libéral ↔ retraité) est effacée ; au chargement, les listes vides ne doivent rien effacer
            const lastProfessionStillApplies =
                (isLiberalJob || isRetired) &&
                (!liberal_job ||
                    lastProfessionOptions.length === 0 ||
                    lastProfessionOptions.some((job) => job.value === parseInt(liberal_job)))
            if (!lastProfessionStillApplies) {
                setValue('liberal_job', '', { shouldValidate: false })
                setValue('medical_job', '', { shouldValidate: false })
            }

            if (currentJobId === UNEMPLOYED_JOB_ID) {
                setValue('searching_job', true, { shouldValidate: false })
            } else {
                setValue('searching_job', false, { shouldValidate: false })
            }

            if (currentJobId === EMPLOYEE_JOB_ID) {
                setValue('job_clerk', true, { shouldValidate: false })
            } else {
                setValue('job_clerk', false, { shouldValidate: false })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentJobId])

        // Effect: Reset medical_job when liberal_job changes
        useEffect(() => {
            if (currentLiberalJobId && currentLiberalJobId !== MEDICAL_JOB_ID) {
                setValue('medical_job', '', { shouldValidate: false })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentLiberalJobId])

        // Effect: Reset job_risk_field when job_risk is false
        useEffect(() => {
            if (job_risk === false) {
                setValue('job_risk_field', '', { shouldValidate: false })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [job_risk])

        useEffect(() => {
            if (risky_sport === false) {
                setValue('risky_sport_name', '', { shouldValidate: false })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [risky_sport])

        // La question n'est posée que sous risque médical : sinon le refus resterait au devis, medical_risk à false
        useEffect(() => {
            if (medical_risk === false) {
                setValue('rejected_by_insurance', false, { shouldValidate: false })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [medical_risk])

        const idSuffix = isMainBorrower ? 'main' : 'co'

        const infoQuestions: LoanFamilyQuestion[] = [
            {
                id: 'birthdate',
                kind: 'select',
                node: (
                    <LoanFormField
                        label="Date de naissance*"
                        chatPrefill={chatMark('birthDate')}
                        error={errors.birthdate}
                        htmlFor={`birthdate-${idSuffix}`}
                    >
                        <DateField
                            name="birthdate"
                            register={register}
                            setValue={setValue}
                            dataTest={`birthdate-${idSuffix}`}
                            autoComplete="bday"
                            usePicker
                            birthdate
                            value={birthdate}
                            minAge={18}
                            maxAge={90}
                            placeholder="Ex. 01/02/1990"
                        />
                    </LoanFormField>
                ),
            },
            {
                id: 'job',
                kind: 'select',
                node: (
                    <LoanFormField
                        label="Statut professionnel*"
                        chatPrefill={chatMark('profession')}
                        tooltip={{
                            title: 'Statut professionnel',
                            text: 'En renseignant votre profession, nous pourrons vous proposer le contrat le plus adapté à votre situation.',
                        }}
                        tooltipId={`job-tooltip-${idSuffix}`}
                        error={errors.job_id}
                        htmlFor={`job-${idSuffix}`}
                    >
                        <AppSelect
                            id={`job-${idSuffix}`}
                            label=""
                            options={jobOptions}
                            variant="v2"
                            placeholder="Sélectionner..."
                            {...register('job_id')}
                        />
                    </LoanFormField>
                ),
            },
            {
                id: 'country',
                kind: 'select',
                node: (
                    <LoanFormField
                        label="Lieu de résidence*"
                        error={errors.address_country}
                        htmlFor={`country-${idSuffix}`}
                    >
                        <AppSelect
                            id={`country-${idSuffix}`}
                            label=""
                            options={countryOptions}
                            placeholder="Sélectionner..."
                            variant="v2"
                            searchable
                            value={address_country}
                            onChange={(e) =>
                                setValue('address_country', String(e.target.value), { shouldValidate: true })
                            }
                        />
                    </LoanFormField>
                ),
            },
        ]

        const activityQuestions: LoanFamilyQuestion[] = [
            ...(isNonExecutiveEmployee
                ? [
                      {
                          id: 'clerk',
                          kind: 'switch' as const,
                          node: (
                              <LoanFormField label="Employé de bureau" row="switch" labelId={`clerk-label-${idSuffix}`}>
                                  <YesNoSwitch
                                      value={normalizeBoolean(job_clerk)}
                                      onChange={(answer) => setValue('job_clerk', answer, { shouldValidate: true })}
                                      ariaLabelledBy={`clerk-label-${idSuffix}`}
                                  />
                              </LoanFormField>
                          ),
                      },
                  ]
                : []),
            ...(showSearchingJob
                ? [
                      {
                          id: 'searching-job',
                          kind: 'switch' as const,
                          node: (
                              <LoanFormField
                                  label="Êtes-vous en recherche d'emploi ?"
                                  tooltip={{
                                      title: "En recherche d'emploi",
                                      text: "Vous êtes considéré « en recherche d'emploi » si vous percevez les <strong>indemnités de Pôle Emploi</strong>. Une attestation vous sera demandée par l'assureur.",
                                  }}
                                  tooltipId={`searching-job-${idSuffix}`}
                                  row="switch"
                                  labelId={`searching-job-label-${idSuffix}`}
                              >
                                  <YesNoSwitch
                                      value={normalizeBoolean(searching_job)}
                                      onChange={(answer) => setValue('searching_job', answer, { shouldValidate: true })}
                                      ariaLabelledBy={`searching-job-label-${idSuffix}`}
                                  />
                              </LoanFormField>
                          ),
                      },
                  ]
                : []),
            ...(isRetired
                ? [
                      {
                          id: 'retired-job',
                          kind: 'select' as const,
                          node: (
                              <LoanFormField
                                  label="Profession exercée dernièrement"
                                  error={errors.liberal_job}
                                  htmlFor={`retired-job-${idSuffix}`}
                                  row="select"
                              >
                                  <AppSelect
                                      id={`retired-job-${idSuffix}`}
                                      label=""
                                      options={retiredJobOptions}
                                      variant="v2"
                                      {...register('liberal_job')}
                                  />
                              </LoanFormField>
                          ),
                      },
                  ]
                : []),
            ...(isLiberalJob
                ? [
                      {
                          id: 'liberal-job',
                          kind: 'select' as const,
                          node: (
                              <LoanFormField
                                  label="Type de profession libérale"
                                  error={errors.liberal_job}
                                  htmlFor={`liberal-job-${idSuffix}`}
                                  row="select"
                              >
                                  <AppSelect
                                      id={`liberal-job-${idSuffix}`}
                                      label=""
                                      options={liberalJobOptions}
                                      variant="v2"
                                      {...register('liberal_job')}
                                  />
                              </LoanFormField>
                          ),
                          child: hasMedicalJob ? (
                              <LoanFormField
                                  label="Type de profession médicale"
                                  error={errors.medical_job}
                                  htmlFor={`medical-job-${idSuffix}`}
                                  row="select"
                                  labelClassName="text-rm-blue-deep"
                              >
                                  <AppSelect
                                      id={`medical-job-${idSuffix}`}
                                      label=""
                                      options={medicalJobOptions}
                                      variant="v2"
                                      {...register('medical_job')}
                                  />
                              </LoanFormField>
                          ) : undefined,
                      },
                  ]
                : []),
            ...(jobMayHaveHandwork
                ? [
                      {
                          id: 'handwork',
                          kind: 'switch' as const,
                          node: (
                              <LoanFormField
                                  label="Manutention"
                                  tooltip={{
                                      title: 'Manutention',
                                      text: 'Activité professionnelle régulière de <strong>manipulation</strong> et/ou <strong>déplacement</strong> de marchandises de plus de 15kg ou de produits dangereux.',
                                  }}
                                  tooltipId={`handwork-${idSuffix}`}
                                  row="switch"
                                  labelId={`handwork-label-${idSuffix}`}
                              >
                                  <YesNoSwitch
                                      value={normalizeBoolean(job_handler)}
                                      onChange={(answer) => setValue('job_handler', answer, { shouldValidate: true })}
                                      ariaLabelledBy={`handwork-label-${idSuffix}`}
                                  />
                              </LoanFormField>
                          ),
                      },
                  ]
                : []),
            ...(jobMayHaveHeightRisk
                ? [
                      {
                          id: 'working-height',
                          kind: 'select' as const,
                          node: (
                              <LoanFormField
                                  label="Travail en hauteur"
                                  htmlFor={`working-height-${idSuffix}`}
                                  row="select"
                              >
                                  <AppSelect
                                      id={`working-height-${idSuffix}`}
                                      label=""
                                      options={workingHeightOptions}
                                      placeholder=""
                                      variant="v2"
                                      {...register('working_height', { valueAsNumber: true })}
                                  />
                              </LoanFormField>
                          ),
                      },
                  ]
                : []),
            ...(showJobRisk
                ? [
                      {
                          id: 'job-risk',
                          kind: 'switch' as const,
                          node: (
                              <LoanFormField
                                  label="Secteur d'activité à risques ?"
                                  row="switch"
                                  labelId={`job-risk-label-${idSuffix}`}
                              >
                                  <YesNoSwitch
                                      value={normalizeBoolean(job_risk)}
                                      onChange={(answer) => setValue('job_risk', answer, { shouldValidate: true })}
                                      ariaLabelledBy={`job-risk-label-${idSuffix}`}
                                  />
                              </LoanFormField>
                          ),
                          child:
                              job_risk === true ? (
                                  <LoanFormField
                                      label="Secteur d'activité*"
                                      error={errors.job_risk_field}
                                      htmlFor={`job-risk-field-${idSuffix}`}
                                      row="select"
                                      labelClassName="text-rm-blue-deep"
                                  >
                                      <AppSelect
                                          id={`job-risk-field-${idSuffix}`}
                                          label=""
                                          options={riskFieldOptions}
                                          placeholder="Sélectionner..."
                                          variant="v2"
                                          {...register('job_risk_field')}
                                      />
                                  </LoanFormField>
                              ) : undefined,
                      },
                  ]
                : []),
            ...(showAnnualMileage
                ? [
                      {
                          id: 'annual-mileage',
                          kind: 'select' as const,
                          node: (
                              <LoanFormField
                                  label="Déplacements professionnels"
                                  tooltip={{
                                      title: 'Déplacements professionnels',
                                      text: 'Seulement pour les <strong>trajets individuels</strong> (voiture, camion, moto...) dans le cadre de votre activité <u>hors trajets</u> quotidiens pour se rendre sur son lieu de travail ou les déplacements en transports en commun (train, métro, avion, bus ...).',
                                  }}
                                  tooltipId={`annual-mileage-${idSuffix}`}
                                  htmlFor={`annual-mileage-${idSuffix}`}
                                  row="select"
                              >
                                  <AppSelect
                                      id={`annual-mileage-${idSuffix}`}
                                      label=""
                                      options={annualMileageOptions}
                                      placeholder=""
                                      variant="v2"
                                      {...register('annual_mileage', { valueAsNumber: true })}
                                  />
                              </LoanFormField>
                          ),
                      },
                  ]
                : []),
        ]

        const healthQuestions: LoanFamilyQuestion[] = [
            {
                id: 'smoker',
                kind: 'switch',
                node: (
                    <LoanFormField
                        label="Fumeur"
                        chatPrefill={chatMark('smoker')}
                        tooltip={{
                            title: 'Fumeur',
                            text: "Vous êtes considéré comme non fumeur si vous n'avez pas fumé depuis <strong>au moins 24 mois</strong>. L'utilisation de <strong>cigarettes électroniques</strong> est assimilé au fait de fumer.",
                        }}
                        tooltipId={`smoker-${idSuffix}`}
                        row="switch"
                        labelId={`smoker-label-${idSuffix}`}
                    >
                        <YesNoSwitch
                            value={normalizeBoolean(smoker)}
                            onChange={(answer) => setValue('smoker', answer, { shouldValidate: true })}
                            ariaLabelledBy={`smoker-label-${idSuffix}`}
                        />
                    </LoanFormField>
                ),
            },
            {
                id: 'medical-risk',
                kind: 'switch',
                node: (
                    <LoanFormField
                        label="Risque médical"
                        tooltip={{
                            title: 'Risque médical',
                            text: "Le fait de préciser que vous avez une <strong>pathologie particulière</strong> (affection longue durée, hospitalisation ...) ou avez eu des <strong>antécédents de santé</strong> nous permettra de vous conseiller l'assureur le plus adapté à votre dossier.",
                        }}
                        tooltipId={`medical-risk-${idSuffix}`}
                        row="switch"
                        labelId={`medical-risk-label-${idSuffix}`}
                    >
                        <YesNoSwitch
                            value={normalizeBoolean(medical_risk)}
                            onChange={(answer) => setValue('medical_risk', answer, { shouldValidate: true })}
                            ariaLabelledBy={`medical-risk-label-${idSuffix}`}
                        />
                    </LoanFormField>
                ),
                child:
                    medical_risk === true ? (
                        <LoanFormField
                            label="Déjà refusé ou ajourné par un assureur"
                            tooltip={{
                                title: 'Risque médical',
                                text: "Si vous avez déjà reçu une <strong>réponse négative</strong> de la part d'un assureur (ou dans le cadre de la convention AERAS au niveau 1, 2 et 3), notre outil va vous proposer un <strong>contrat spécialisé</strong> pour que vous ayez le plus de chance de recevoir une proposition d'assurance.",
                            }}
                            tooltipId={`rejected-insurance-${idSuffix}`}
                            row="switch"
                            labelId={`rejected-insurance-label-${idSuffix}`}
                            labelClassName="text-rm-blue-deep"
                        >
                            <YesNoSwitch
                                value={normalizeBoolean(rejected_by_insurance)}
                                onChange={(answer) =>
                                    setValue('rejected_by_insurance', answer, { shouldValidate: true })
                                }
                                ariaLabelledBy={`rejected-insurance-label-${idSuffix}`}
                            />
                        </LoanFormField>
                    ) : undefined,
            },
            {
                id: 'risky-sport',
                kind: 'switch',
                node: (
                    <LoanFormField
                        label="Pratiquez-vous un sport à risque ?"
                        row="switch"
                        labelId={`risky-sport-label-${idSuffix}`}
                    >
                        <YesNoSwitch
                            value={normalizeBoolean(risky_sport)}
                            onChange={(answer) => setValue('risky_sport', answer, { shouldValidate: true })}
                            ariaLabelledBy={`risky-sport-label-${idSuffix}`}
                        />
                    </LoanFormField>
                ),
                child:
                    risky_sport === true ? (
                        <LoanFormField
                            label="Sport pratiqué*"
                            error={errors.risky_sport_name}
                            htmlFor={`risky-sport-name-${idSuffix}`}
                            row="select"
                            labelClassName="text-rm-blue-deep"
                        >
                            <AppSelect
                                id={`risky-sport-name-${idSuffix}`}
                                label=""
                                options={riskySportOptions}
                                placeholder="Sélectionner..."
                                variant="v2"
                                searchable
                                {...register('risky_sport_name')}
                            />
                        </LoanFormField>
                    ) : undefined,
            },
        ]

        return (
            <FormProvider {...methods}>
                <div
                    className={`loan-comparator-borrower-quotation-form flex flex-col gap-y-4 md:gap-y-[28px] ${className}`}
                    data-testid="borrower-form"
                >
                    <LoanFieldFamily title="Vos informations" columns={3} questions={infoQuestions} />
                    <LoanFieldFamily
                        title="Votre activité"
                        hint="dépend du statut sélectionné"
                        tone="cream"
                        questions={activityQuestions}
                    />
                    <LoanFieldFamily title="Votre santé" rule questions={healthQuestions} />
                </div>
            </FormProvider>
        )
    }
)

LoanComparatorQuotationBorrowerForm.displayName = 'LoanComparatorQuotationBorrowerForm'

export default LoanComparatorQuotationBorrowerForm
